import { Extension } from "@tiptap/core";
import BoldExtension from "@tiptap/extension-bold";
import BulletExtension from "@tiptap/extension-bullet-list";
import DocumentExtension from "@tiptap/extension-document";
import HeadingExtension from "@tiptap/extension-heading";
import HistoryExtension from "@tiptap/extension-history";
import ItalicExtension from "@tiptap/extension-italic";
import LinkExtension from "@tiptap/extension-link";
import ListItemExtension from "@tiptap/extension-list-item";
import OrderedListExtension from "@tiptap/extension-ordered-list";
import ParagraphExtension from "@tiptap/extension-paragraph";
import PlaceholderExtension from "@tiptap/extension-placeholder";
import StrikeExtension from "@tiptap/extension-strike";
import TextExtension from "@tiptap/extension-text";
import UnderlineExtension from "@tiptap/extension-underline";
import { Editor, useEditor } from "@tiptap/react";

const DisableEnter = Extension.create({
  addKeyboardShortcuts() {
    return {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      Enter: () => true
    };
  }
});

type CreateEditorArgs = {
  autofocus: boolean;
  disabled?: boolean;
  onBlur?: () => void;
  onChange: ((value: string) => void) | undefined;
  onFocus?: () => void;
  placeholder: string | undefined;
  preventEnter?: boolean;
  value: string | undefined | null;
};

const useRichTextarea = ({
  autofocus,
  disabled = false,
  onBlur,
  onChange,
  onFocus,
  placeholder,
  preventEnter = false,
  value
}: CreateEditorArgs): Editor | null => {
  const extensions = [
    BoldExtension,
    BulletExtension,
    DocumentExtension,
    HeadingExtension.configure({
      levels: [1, 2, 3]
    }),
    HistoryExtension,
    ItalicExtension,
    LinkExtension.configure({
      autolink: false,
      openOnClick: false
    }),
    ListItemExtension,
    OrderedListExtension,
    ParagraphExtension,
    PlaceholderExtension.configure({
      placeholder
    }),
    StrikeExtension,
    TextExtension,
    UnderlineExtension
  ];

  if (preventEnter) {
    extensions.push(DisableEnter);
  }

  return useEditor({
    autofocus,
    content: value,
    editable: !disabled,
    extensions,
    onBlur: () => {
      if (onBlur) {
        onBlur();
      }
    },
    onFocus: () => {
      if (onFocus) {
        onFocus();
      }
    },
    onUpdate: editor => {
      if (onChange) {
        if (editor.editor.getText() === "") {
          onChange("");
        } else {
          onChange(editor.editor.getHTML());
        }
      }
    }
  });
};

export default useRichTextarea;
